import React, { useState, useEffect, useCallback } from 'react';
import { Users, TrendingUp, TrendingDown, Activity, Zap, Clock, MessageCircle, Waves, BarChart3, ShieldAlert, RefreshCw, Ticket } from 'lucide-react';
import { motion } from 'framer-motion';
import { authFetch, API } from '../../utils/api';


function KpiCard({ icon: Icon, label, value, sub, trend, trendLabel, color = 'text-primary', delay = 0 }) {
  const isUp = trend > 0;
  const isDown = trend < 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: delay * 0.04 }}
      className="bg-white/[0.025] border border-white/5 rounded-xl p-3"
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`w-3 h-3 ${color} opacity-50`} />
        <span className="text-[8px] uppercase tracking-widest text-muted-foreground/40 leading-none">{label}</span>
      </div>
      <div className="flex items-end justify-between">
        <p className={`text-lg font-heading font-bold ${color} leading-none`}>{value}</p>
        {trend !== undefined && trend !== 0 && (
          <span className={`text-[9px] inline-flex items-center gap-0.5 ${isUp ? 'text-green-400' : isDown ? 'text-red-400' : 'text-muted-foreground/40'}`}>
            {isUp ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
            {Math.abs(trend)}%
            {trendLabel && <span className="text-muted-foreground/30 ml-0.5">{trendLabel}</span>}
          </span>
        )}
      </div>
      {sub && <p className="text-[9px] text-muted-foreground/40 mt-1">{sub}</p>}
    </motion.div>
  );
}

function MiniBarChart({ data, dataKey, labelKey = 'date', height = 64, color = 'from-primary/30 to-primary/10' }) {
  if (!data || data.length === 0) return <p className="text-[9px] text-muted-foreground/30 text-center py-4">no data</p>;
  const max = Math.max(...data.map(d => d[dataKey]), 1);
  const showEvery = data.length > 14 ? Math.ceil(data.length / 7) : data.length > 7 ? 2 : 1;

  return (
    <div className="flex items-end gap-px" style={{ height }} data-testid={`chart-${dataKey}`}>
      {data.map((d, i) => {
        const h = (d[dataKey] / max) * 100;
        return (
          <div key={i} className="flex-1 flex flex-col items-center group relative min-w-0">
            <div className="w-full relative" style={{ height: `${height - 16}px` }}>
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: `${Math.max(h, 2)}%` }}
                transition={{ duration: 0.4, delay: i * 0.015 }}
                className={`absolute bottom-0 w-full rounded-t-sm bg-gradient-to-t ${color}`}
              />
            </div>
            {i % showEvery === 0 && (
              <span className="text-[6px] text-muted-foreground/25 mt-0.5 truncate w-full text-center">
                {d[labelKey]?.slice(5) || ''}
              </span>
            )}
            <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-bg-paper border border-white/10 rounded-md px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap">
              <p className="text-[8px] text-white">{d[dataKey]}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RetentionCard({ label, data, delay = 0 }) {
  const rate = data?.rate || 0;
  const size = data?.cohort_size || 0;
  const returned = data?.returned || 0;
  const barColor = rate >= 40 ? 'bg-green-400' : rate >= 20 ? 'bg-yellow-400' : 'bg-red-400';

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="flex-1 text-center"
    >
      <p className="text-[9px] text-muted-foreground/40 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-lg font-heading font-bold ${rate >= 40 ? 'text-green-400' : rate >= 20 ? 'text-yellow-400' : 'text-red-400'}`}>
        {rate}%
      </p>
      <div className="h-1 bg-white/5 rounded-full mt-1.5 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${rate}%` }}
          transition={{ duration: 0.6, delay: delay + 0.2 }}
          className={`h-full rounded-full ${barColor}`}
        />
      </div>
      <p className="text-[8px] text-muted-foreground/30 mt-1">{returned}/{size} users</p>
    </motion.div>
  );
}

function FeatureRow({ feature, calls, users, maxCalls, delay = 0 }) {
  const pct = maxCalls > 0 ? (calls / maxCalls) * 100 : 0;
  return (
    <motion.div
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay }}
      className="flex items-center gap-2"
    >
      <span className="text-[9px] text-muted-foreground/60 w-20 text-right truncate flex-shrink-0">{feature}</span>
      <div className="flex-1 relative h-4 bg-white/[0.02] rounded overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5, delay: delay + 0.1 }}
          className="h-full bg-gradient-to-r from-secondary/30 to-secondary/10 rounded"
        />
        <div className="absolute inset-0 flex items-center justify-between px-1.5">
          <span className="text-[8px] font-bold text-white/70">{calls.toLocaleString()}</span>
          <span className="text-[7px] text-muted-foreground/40">{users}u</span>
        </div>
      </div>
    </motion.div>
  );
}

function LevelPill({ level, count, total, delay = 0 }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const colors = {
    beginner: 'bg-green-400/15 text-green-400 border-green-400/20',
    intermediate: 'bg-blue-400/15 text-blue-400 border-blue-400/20',
    advanced: 'bg-purple-400/15 text-purple-400 border-purple-400/20',
    pro: 'bg-amber-400/15 text-amber-400 border-amber-400/20',
  };
  const cls = colors[level] || 'bg-white/5 text-muted-foreground border-white/10';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay }}
      className={`flex items-center justify-between px-3 py-2 rounded-lg border ${cls}`}
    >
      <span className="text-[10px] font-medium capitalize">{level || 'unknown'}</span>
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold">{count}</span>
        <span className="text-[8px] opacity-60">{pct}%</span>
      </div>
    </motion.div>
  );
}

export default function AdminDashboard() {
  const [kpis, setKpis] = useState(null);
  const [growthChart, setGrowthChart] = useState(null);
  const [engagementChart, setEngagementChart] = useState(null);
  const [retention, setRetention] = useState(null);
  const [surfLevels, setSurfLevels] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [kpiRes, growthRes, engRes, retRes, lvlRes] = await Promise.all([
        authFetch(`${API}/api/admin/dashboard/kpis`),
        authFetch(`${API}/api/admin/dashboard/user-growth-chart?days=30`),
        authFetch(`${API}/api/admin/dashboard/engagement-chart?days=14`),
        authFetch(`${API}/api/admin/dashboard/retention`),
        authFetch(`${API}/api/admin/dashboard/surf-level-distribution`),
      ]);
      if (kpiRes.ok) setKpis(await kpiRes.json());
      if (growthRes.ok) setGrowthChart(await growthRes.json());
      if (engRes.ok) setEngagementChart(await engRes.json());
      if (retRes.ok) setRetention(await retRes.json());
      if (lvlRes.ok) setSurfLevels(await lvlRes.json());
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading && !kpis) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="dashboard-loading">
        <div className="flex space-x-2">
          <div className="w-2 h-2 bg-primary rounded-full typing-dot" />
          <div className="w-2 h-2 bg-primary rounded-full typing-dot" />
          <div className="w-2 h-2 bg-primary rounded-full typing-dot" />
        </div>
      </div>
    );
  }

  const ug = kpis?.user_growth || {};
  const eng = kpis?.engagement || {};
  const api = kpis?.api_health || {};
  const content = kpis?.content || {};
  const support = kpis?.support || {};
  const features = kpis?.feature_adoption || [];
  const maxFeatureCalls = features.length > 0 ? Math.max(...features.map(f => f.calls)) : 1;
  const totalLevelUsers = (surfLevels?.distribution || []).reduce((s, d) => s + d.count, 0);

  return (
    <div data-testid="admin-dashboard-page" className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground/40">SaaS Health</p>
        </div>
        <button onClick={fetchAll} className="p-1.5 hover:bg-white/10 rounded-full transition-colors" data-testid="refresh-dashboard">
          <RefreshCw className={`w-3.5 h-3.5 text-muted-foreground/50 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* ── ROW 1: User Growth KPIs ──────────────────────────────── */}
      <div>
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground/30 mb-2">User Growth</p>
        <div className="grid grid-cols-2 gap-2">
          <KpiCard icon={Users} label="Total Users" value={ug.total_users || 0} sub={`${ug.banned_users || 0} banned`} delay={0} />
          <KpiCard icon={TrendingUp} label="New (7d)" value={ug.new_7d || 0} trend={ug.growth_wow} trendLabel="WoW" color="text-green-400" delay={1} />
          <KpiCard icon={TrendingUp} label="New (30d)" value={ug.new_30d || 0} trend={ug.growth_mom} trendLabel="MoM" color="text-secondary" delay={2} />
          <KpiCard icon={Activity} label="DAU / MAU" value={`${eng.dau || 0} / ${eng.mau || 0}`} sub={`${eng.stickiness || 0}% stickiness`} color="text-blue-400" delay={3} />
        </div>
      </div>

      {/* ── User Growth Chart ────────────────────────────────────── */}
      <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground/40">New Signups (30d)</p>
          <p className="text-[8px] text-muted-foreground/25">{(growthChart?.data || []).length} days</p>
        </div>
        <MiniBarChart data={growthChart?.data || []} dataKey="new_users" height={56} />
      </div>

      {/* ── Engagement Chart ─────────────────────────────────────── */}
      <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground/40">Daily Active Users (14d)</p>
          <div className="flex items-center gap-2">
            <span className="text-[9px] text-muted-foreground/30">WAU: {eng.wau || 0}</span>
          </div>
        </div>
        <MiniBarChart data={engagementChart?.data || []} dataKey="active_users" height={56} color="from-blue-400/30 to-blue-400/10" />
      </div>

      {/* ── ROW 2: Engagement KPIs ───────────────────────────────── */}
      <div>
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground/30 mb-2">Engagement & API</p>
        <div className="grid grid-cols-2 gap-2">
          <KpiCard icon={Zap} label="API Calls (7d)" value={(api.calls_7d || 0).toLocaleString()} sub={`${api.calls_today || 0} today`} color="text-primary" delay={4} />
          <KpiCard icon={Clock} label="Avg Latency" value={`${api.avg_latency_ms || 0}ms`} sub={`${api.error_rate_pct || 0}% errors`} color={api.error_rate_pct > 5 ? 'text-red-400' : 'text-green-400'} delay={5} />
          <KpiCard icon={MessageCircle} label="AI Chats (7d)" value={content.chats_7d || 0} sub={`${content.ai_conversations || 0} total`} color="text-purple-400" delay={6} />
          <KpiCard icon={Waves} label="Sessions (7d)" value={content.sessions_7d || 0} sub={`${content.surf_sessions || 0} total`} color="text-cyan-400" delay={7} />
        </div>
      </div>

      {/* ── Retention ────────────────────────────────────────────── */}
      {retention && (
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground/40 mb-3">Retention</p>
          <div className="flex gap-3">
            <RetentionCard label="Day 1" data={retention.d1_retention} delay={0.1} />
            <RetentionCard label="Day 7" data={retention.d7_retention} delay={0.15} />
            <RetentionCard label="Day 30" data={retention.d30_retention} delay={0.2} />
          </div>
        </div>
      )}

      {/* ── Content Metrics ──────────────────────────────────────── */}
      <div>
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground/30 mb-2">Content & Activity</p>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Boards', value: content.boards || 0, sub: `+${content.boards_7d || 0} 7d`, icon: BarChart3 },
            { label: 'Plans', value: content.planned_sessions || 0, icon: Clock },
            { label: 'Crew', value: content.crew_invites || 0, icon: Users },
          ].map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.04 }}
              className="bg-white/[0.025] border border-white/5 rounded-xl p-2.5 text-center"
            >
              <m.icon className="w-3 h-3 text-muted-foreground/30 mx-auto mb-1" />
              <p className="text-sm font-heading font-bold text-foreground">{m.value}</p>
              <p className="text-[8px] text-muted-foreground/40">{m.label}</p>
              {m.sub && <p className="text-[7px] text-green-400/60 mt-0.5">{m.sub}</p>}
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Support Health ───────────────────────────────────────── */}
      <div>
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground/30 mb-2">Support Health</p>
        <div className="grid grid-cols-2 gap-2">
          <KpiCard icon={Ticket} label="Open Tickets" value={support.open_tickets || 0} sub={`${support.total_tickets || 0} total`} color={support.open_tickets > 5 ? 'text-yellow-400' : 'text-green-400'} delay={8} />
          <KpiCard icon={ShieldAlert} label="Pending Appeals" value={support.pending_appeals || 0} color={support.pending_appeals > 0 ? 'text-amber-400' : 'text-muted-foreground'} delay={9} />
        </div>
      </div>

      {/* ── Feature Adoption ─────────────────────────────────────── */}
      {features.length > 0 && (
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3">
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground/40">Feature Adoption (7d)</p>
            <p className="text-[8px] text-muted-foreground/25">calls / unique users</p>
          </div>
          <div className="space-y-1.5">
            {features.slice(0, 10).map((f, i) => (
              <FeatureRow
                key={f.feature}
                feature={f.feature}
                calls={f.calls}
                users={f.unique_users}
                maxCalls={maxFeatureCalls}
                delay={0.05 + i * 0.03}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Surf Level Distribution ──────────────────────────────── */}
      {surfLevels?.distribution && surfLevels.distribution.length > 0 && (
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3">
          <p className="text-[9px] uppercase tracking-widest text-muted-foreground/40 mb-2.5">User Surf Levels</p>
          <div className="space-y-1.5">
            {surfLevels.distribution.map((d, i) => (
              <LevelPill key={d.level} level={d.level} count={d.count} total={totalLevelUsers} delay={0.05 + i * 0.04} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
